import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChatMessage } from "@gb/contracts";
import { api, errorMessage } from "../../api/client";

export const chatKeys = {
  base: (tenantId: string, projectId: string) =>
    ["tenants", tenantId, "projects", projectId, "chat"] as const,
  thread: (tenantId: string, projectId: string, threadId: string) =>
    [...chatKeys.base(tenantId, projectId), threadId] as const,
};

const base = (tenantId: string, projectId: string) =>
  ["tenants", tenantId, "projects", projectId, "chat"] as const;

export function useThreads(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: base(tenantId, projectId),
    queryFn: async () => {
      const res = await api.chat.threads({
        params: { tenantId, projectId },
        query: { limit: 100, offset: 0 },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/**
 * How long a question may wait for its answer before the screen stops asking:
 * the agent's 20 minutes, its wrap-up's 3 and 2 more, the same span after
 * which the server's sweep stores a silent question as unfinished (§1.11).
 */
export const ANSWER_WAIT_MS = 25 * 60_000;

/** Whether a thread's question waits for an answer that may still be running. */
export function awaitingAnswer(messages: readonly ChatMessage[] | undefined, now = Date.now()): boolean {
  if (!messages || messages.length === 0) return false;
  if (messages.some((message) => message.role === "assistant")) return false;
  const question = messages.find((message) => message.role === "user");
  return question !== undefined && now - Date.parse(question.createdAt) < ANSWER_WAIT_MS;
}

/**
 * A thread's turns. While its question waits for an answer, read them again
 * every 2 seconds: the answer runs on when its reader leaves, so a reader who
 * opens the thread mid-answer sees it arrive (Phase 5, §1.11 of the design).
 */
export function useThreadMessages(tenantId: string, projectId: string, threadId: string) {
  return useQuery({
    queryKey: [...base(tenantId, projectId), threadId],
    refetchInterval: (query) => (awaitingAnswer(query.state.data) ? 2_000 : false),
    queryFn: async () => {
      const res = await api.chat.messages({ params: { tenantId, projectId, threadId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.messages;
    },
  });
}

/**
 * **Read once more when the polling stops** (2026-09-27). The last poll can
 * be up to 2 seconds older than the answer, from before the review's row and
 * the last turn's steps and calls were written. The thread's poll refetches
 * the thread only, so nothing else reads them again.
 */
function useReadOnceMoreWhenPollingStops(threadId: string, poll: boolean, enabled: boolean, refetch: () => unknown) {
  // The thread whose rows were polled.
  const polledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    if (poll) polledFor.current = threadId;
    else if (polledFor.current === threadId) {
      polledFor.current = null;
      void refetch();
    }
  }, [enabled, poll, threadId, refetch]);
}

/**
 * Every step of the thread's answer, with its input and its output whole
 * (Phase 5, 2026-09-26, R118). Polled every 2 seconds while the thread has
 * no answer, so a reader who opens a thread mid-answer sees the steps so far:
 * the answer runs on when its reader leaves (§1.11 of the design). Read once
 * more when the polling stops.
 */
export function useAnswerSteps(
  tenantId: string,
  projectId: string,
  threadId: string,
  options: { poll: boolean; enabled?: boolean },
) {
  const enabled = options.enabled ?? true;
  const query = useQuery({
    queryKey: [...chatKeys.thread(tenantId, projectId, threadId), "steps"],
    enabled,
    refetchInterval: options.poll ? 2_000 : false,
    queryFn: async () => {
      const res = await api.chat.listSteps({ params: { tenantId, projectId, threadId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.steps;
    },
  });
  useReadOnceMoreWhenPollingStops(threadId, options.poll, enabled, query.refetch);
  return query;
}

/**
 * Every model call of the thread's answer, without its bodies (2026-09-27):
 * what "How this answer was made" places between the steps. Read as the
 * steps are: once the thread has a question, every 2 seconds while its
 * answer runs, and once more when the polling stops. An answer that failed
 * or was refused with no text is never stored, and its calls are what
 * support most needs to read. A call's bodies are read when it is opened
 * (`useRun`).
 */
export function useAnswerRuns(
  tenantId: string,
  projectId: string,
  threadId: string,
  options: { poll: boolean; enabled: boolean },
) {
  const query = useQuery({
    queryKey: [...chatKeys.thread(tenantId, projectId, threadId), "runs"],
    enabled: options.enabled,
    refetchInterval: options.poll ? 2_000 : false,
    queryFn: async () => {
      const res = await api.chat.listRuns({ params: { tenantId, projectId, threadId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
  useReadOnceMoreWhenPollingStops(threadId, options.poll, options.enabled, query.refetch);
  return query;
}

/**
 * Stop the answer running on a thread (Phase 5, 2026-09-26, §1.11). Stored on
 * the question, so it stops the answer whichever task runs it. `requested` is
 * false when there was nothing left to stop.
 */
export async function cancelAsk(tenantId: string, projectId: string, threadId: string): Promise<boolean> {
  const res = await api.chat.cancelAsk({ params: { tenantId, projectId, threadId } });
  if (res.status !== 200) throw new Error(errorMessage(res));
  return res.body.requested;
}

/** How a tenant's answers ended, counted (R119): by project and by day when given. */
export function useAnswerCounts(
  tenantId: string,
  filter: { projectId?: string; from?: string; to?: string },
) {
  return useQuery({
    queryKey: ["tenants", tenantId, "chat", "answer-counts", filter],
    queryFn: async () => {
      const res = await api.chat.answerCounts({ params: { tenantId }, query: filter });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useCreateThread(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (question?: string) => {
      const res = await api.chat.createThread({
        params: { tenantId, projectId },
        body: question ? { question } : {},
      });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.thread;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: chatKeys.base(tenantId, projectId) }),
  });
}

export function useSetThreadVisibility(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      threadId,
      sharedWithTenant,
    }: {
      threadId: string;
      sharedWithTenant: boolean;
    }) => {
      const res = await api.chat.setThreadVisibility({
        params: { tenantId, projectId, threadId },
        body: { sharedWithTenant },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.thread;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: chatKeys.base(tenantId, projectId) }),
  });
}
